import type { ErrorBody } from "./types";

/** A response from the server that was not 2xx. `message` is the API's text, shown verbatim
 * (§2.2), e.g. 403 "A manager must void this sale". */
export class ApiError extends Error {
  readonly status: number;
  readonly error: string | undefined;
  constructor(status: number, message: string, error?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.error = error;
  }
}

/** The request never reached the server (offline, DNS, connection refused, CORS). This is
 * the only failure that may fall back to the offline queue (§16.1); business errors never do. */
export class NetworkError extends Error {
  /** Brand: without it ApiError is structurally a NetworkError, and narrowing breaks. */
  readonly kind = "network" as const;
  constructor(message = "Cannot reach the server. Check the connection and try again.") {
    super(message);
    this.name = "NetworkError";
  }
}

export const isApiError = (e: unknown): e is ApiError => e instanceof ApiError;
export const isNetworkError = (e: unknown): e is NetworkError => e instanceof NetworkError;

const FALLBACK: Record<number, string> = {
  400: "Please check the details and try again.",
  401: "Your session has ended. Please sign in again.",
  403: "You don't have permission to do that.",
  404: "That record could not be found.",
  409: "That conflicts with a change someone else made. Refresh and try again.",
  413: "That file is too large.",
  423: "This account is locked.",
  429: "Too many attempts. Please wait a moment and try again.",
};

export async function errorFromResponse(res: Response): Promise<ApiError> {
  let body: ErrorBody | null = null;
  try {
    const text = await res.text();
    body = text ? (JSON.parse(text) as ErrorBody) : null;
  } catch {
    body = null;
  }
  const message = typeof body?.message === "string" && body.message.trim() ? body.message : (FALLBACK[res.status] ?? (res.status >= 500 ? "The server had a problem. Please try again." : "Something went wrong. Please try again."));
  return new ApiError(res.status, message, body?.error);
}
